import { useEffect, useState, useCallback, memo, useRef } from 'react';
import { api } from '../api/api';
import SendMessage from './SendMessage';
import Connect4Board from './Connect4Board';

// Shows a placeholder shimmer while the signed URL is fetched and the image loads.
const ChatImage = memo(function ChatImage({ objectKey }) {
  const [src, setSrc] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.getImageUrl(objectKey).then(setSrc).catch(console.error);
  }, [objectKey]);

  return (
    <div className="message-image-wrapper">
      {!loaded && <div className="image-placeholder" />}
      {src && (
        <img
          src={src}
          alt="attachment"
          className={`message-image${loaded ? ' loaded' : ''}`}
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
        />
      )}
    </div>
  );
});

function StartDirectChatForm({ currentUser, onSelectChat }) {
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otherUsername = username.trim();
    if (!otherUsername) return;

    if (otherUsername.toLowerCase() === currentUser.username.toLowerCase()) {
      setError("You cannot create a 1-on-1 chat with yourself.");
      return;
    }

    setError('');
    setLoading(true);

    try {
      // Auto-generate chat title
      const chatTitle = `${currentUser.username} & ${otherUsername}`;

      // Create Chat
      const chatResult = await api.createChat({
        sender_name: currentUser.username,
        title: chatTitle,
      });

      if (chatResult.res !== 'success') {
        throw new Error('Failed to create chat');
      }
      const chat_id = chatResult.data.message_id;

      // Link the other participant
      const linkResult = await api.linkUserToChat(otherUsername, chat_id);
      if (linkResult.res !== 'success') {
        throw new Error(`User "${otherUsername}" not found.`);
      }

      // Success
      setUsername('');
      // Trigger chat list refresh
      window.dispatchEvent(new CustomEvent('refreshChats'));
      if (onSelectChat) {
        onSelectChat(chat_id);
      }
    } catch (err) {
      setError(err.message || 'Failed to start chat');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="quick-chat-container">
      <h4>Or Start a 1-on-1 Chat</h4>
      <form onSubmit={handleSubmit} className="quick-chat-form">
        <div className="quick-chat-input-group">
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Enter friend's username"
            disabled={loading}
            required
          />
          <button type="submit" disabled={loading || !username.trim()}>
            {loading ? 'Starting...' : 'Start Chat'}
          </button>
        </div>
        {error && <p className="error quick-chat-error">{error}</p>}
      </form>
    </div>
  );
}

function ChatView({ chatId, currentUser, onSelectChat, onToggleSidebar, sidebarOpen }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  // Navigation stack of parents. The first entry is the chat's root message;
  // each additional entry is a message the user opened as a sub-chat.
  const [parentStack, setParentStack] = useState(() =>
    chatId ? [{ id: chatId, label: 'Chat' }] : []
  );
  const messagesEndRef = useRef(null);
  const prevMessagesRef = useRef([]);

  const currentParent = parentStack.length ? parentStack[parentStack.length - 1] : null;
  const currentParentId = currentParent ? currentParent.id : null;

  // Reset the navigation stack whenever the selected chat changes.
  useEffect(() => {
    setParentStack(chatId ? [{ id: chatId, label: 'Chat' }] : []);
  }, [chatId]);

  const loadMessages = useCallback(async () => {
    if (!currentParentId) {
      return;
    }
    try {
      const result = await api.getMessages(currentParentId);
      if (result.status === 'success' && result.payload) {
        setMessages((prevMessages) => {
          // Re-render if anything changed — IDs, order, or content (e.g. a Connect4 move).
          if (JSON.stringify(prevMessages) === JSON.stringify(result.payload)) {
            return prevMessages;
          }
          return result.payload;
        });
      } else {
        setMessages([]);
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [currentParentId]);

  // Clear messages and show the loading state when navigating to a new parent.
  useEffect(() => {
    setMessages([]);
    setLoading(true);
  }, [currentParentId]);

  useEffect(() => {
    if (!currentParentId) {
      return;
    }

    loadMessages();

    const handleRefreshMessages = () => {
      loadMessages();
    };
    window.addEventListener('refreshMessages', handleRefreshMessages);

    const intervalId = setInterval(() => {
      loadMessages();
    }, 3000);

    return () => {
      window.removeEventListener('refreshMessages', handleRefreshMessages);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [currentParentId, loadMessages]);

  const handleRefresh = () => {
    loadMessages();
  };

  // Open the children of a message as a nested sub-chat.
  const openSubChat = (msg) => {
    const text = msg.content ? (msg.content.title || msg.content.text || '').trim() : '';
    const label = text ? text.slice(0, 30) : 'Sub-chat';
    setParentStack((stack) => [...stack, { id: msg.message_id, label }]);
  };

  // Navigate back up the sub-chat stack to the given depth.
  const goToParent = (index) => {
    setParentStack((stack) => stack.slice(0, index + 1));
  };

  // Scroll to bottom only when new messages are added (not when existing messages update, e.g. Connect4 moves)
  useEffect(() => {
    if (!messagesEndRef.current) return;

    const prev = prevMessagesRef.current;
    const isNewMessage = messages.length > prev.length ||
      (messages.length === prev.length && messages.some((msg, i) => msg.message_id !== prev[i]?.message_id));

    if (isNewMessage) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    prevMessagesRef.current = messages;
  }, [messages]);

  if (!chatId) {
    return (
      <div className="chat-view empty">
        <p>Select a chat to view messages</p>
        <p className="hint">You can create a chat by typing participants' usernames and a chat name above.</p>
        <StartDirectChatForm currentUser={currentUser} onSelectChat={onSelectChat} />
      </div>
    );
  }

  return (
    <div className="chat-view">
      <div className="chat-header">
        {parentStack.length > 1 ? (
          <div className="subchat-nav">
            <button
              type="button"
              className="link-btn back-btn"
              onClick={() => goToParent(parentStack.length - 2)}
            >
              ← Back
            </button>
            <h3>{currentParent.label}</h3>
          </div>
        ) : (
          <div className="chat-header-main">
            <h3>Chat</h3>
            {onToggleSidebar && (
              <button
                type="button"
                className="toggle-sidebar-btn"
                onClick={onToggleSidebar}
                title="Toggle chats list"
                aria-label="Toggle chats list"
              >
                📋 {sidebarOpen ? 'Hide Chats' : 'Chats'}
              </button>
            )}
          </div>
        )}
      </div>
      <div className="messages-container">
        {loading ? (
          <p>Loading messages...</p>
        ) : messages.length === 0 ? (
          <p className="no-messages">No messages yet. Be the first to send one!</p>
        ) : (
          messages.map((msg, index) => {
            const isConnect4 = msg.content && Array.isArray(msg.content.grid);
            return (
              <div
                key={msg.message_id || index}
                className={`message ${msg.sender_name === currentUser.username ? 'own' : 'other'}${isConnect4 ? ' connect4-message' : ''}`}
              >
                {isConnect4 ? (
                  <Connect4Board
                    msg={msg}
                    currentUser={currentUser}
                    onMoveSent={handleRefresh}
                  />
                ) : (
                  <>
                    {msg.content && (msg.content.title || msg.content.text) && (msg.content.title || msg.content.text).trim() && (
                      <div className="message-content">{msg.content.title || msg.content.text}</div>
                    )}
                    {msg.content && msg.content.url && <ChatImage objectKey={msg.content.url} />}
                  </>
                )}
                <div className="message-meta">
                  <span className="message-sender">
                    {msg.sender_name === currentUser.username ? 'You' : msg.sender_name}
                  </span>
                  <span className="message-time">
                    {msg.sent_at ? new Date(msg.sent_at).toLocaleString() : ''}
                  </span>
                </div>
                {msg.message_id && (
                  <button
                    type="button"
                    className="link-btn subchat-btn"
                    onClick={() => openSubChat(msg)}
                    title="Open a sub-chat from this message"
                  >
                    💬 Open sub-chat
                  </button>
                )}
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>
      <SendMessage chatId={currentParentId} senderName={currentUser.username} onMessageSent={handleRefresh} />
    </div>
  );
}

export default ChatView;
